import { faArrowDown, faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import type { CSSProperties, HTMLProps } from 'react';
import { Children, createContext, isValidElement, useContext, useMemo } from 'react';

type NavigateOptions = Record<string, any>;
function BaseTable({ className, children, ...rest }: HTMLProps<HTMLTableElement>) {
	return (
		<div
			className={`
			overflow-auto 
			${className?.includes('rounded-none') ? '' : 'rounded-lg'}
			${className?.includes('border-0') ? '' : 'border'} 
			border-gray-300
		`}
		>
			<table className={clsx('w-full', className)} {...rest}>
				{children}
			</table>
		</div>
	);
}

export function Table(props: HTMLProps<HTMLTableElement>) {
	return <BaseTable {...props} />;
}

export interface TableRowProps extends HTMLProps<HTMLTableRowElement> {
	enableHover?: boolean;
}

export function TableRow({ className, enableHover = false, ...rest }: TableRowProps) {
	const classesToRemove = className?.match(/(?:bg-white|bg-gray-50)/g) || [];
	const classesToAdd = classesToRemove.length === 2 ? [] : classesToRemove.filter((c) => c !== 'bg-white' && c !== 'odd:bg-gray-50');
	return <tr className={clsx(classesToAdd, className, ...classesToRemove.map((c) => `-${c}`), enableHover ? 'hover:bg-gray-200' : '')} {...rest} />;
}

interface TableHeaderRowContextData {
	totalWidth: number;
}

const TableHeaderRowContext = createContext<TableHeaderRowContextData>({} as TableHeaderRowContextData);

function useTotalWidth(): number {
	const context = useContext(TableHeaderRowContext);
	return context.totalWidth;
}

interface TableHeaderChildProps {
	width?: number;
}

export function TableHeaderRow({ className, children, ...rest }: HTMLProps<HTMLTableRowElement>) {
	const totalWidth = Children.toArray(children).reduce((prev: number, child) => {
		if (isValidElement<TableHeaderChildProps>(child)) {
			return prev + (child.props.width || 0);
		}
		return prev;
	}, 0);

	const contextData = useMemo<TableHeaderRowContextData>(
		() => ({
			totalWidth
		}),
		[totalWidth]
	);

	return (
		<TableRow className={className} {...rest}>
			<TableHeaderRowContext.Provider value={contextData}>{children}</TableHeaderRowContext.Provider>
		</TableRow>
	);
}

export interface TableHeaderCellProps extends HTMLProps<HTMLTableCellElement> {
	fixedWidth?: number;
	width?: number;
}

export function TableHeaderCell({ fixedWidth, width, className, ...rest }: TableHeaderCellProps) {
	const totalWidth = useTotalWidth();

	function calculateWidth(): CSSProperties {
		if (fixedWidth) {
			return { minWidth: fixedWidth, maxWidth: fixedWidth };
		}

		if (width) {
			return { width: `${(width / totalWidth) * 100.0}%` };
		}

		return {};
	}

	return (
		<th style={calculateWidth()} className={clsx('whitespace-nowrap bg-white px-2.5 py-2 text-left text-sm font-normal', className)} {...rest} />
	);
}

export function TableCell({ className, ...rest }: HTMLProps<HTMLTableCellElement>) {
	const borderTClass = className?.includes('border-t-0') ? '' : 'border-t border-gray-200';
	return <td className={clsx('break-words', borderTClass, 'border-x-0 p-2.5', className)} {...rest} />;
}

export type Direction = 'asc' | 'desc' | undefined;

export interface SortableTableHeaderCellProps extends TableHeaderCellProps {
	columnName: string;
	onSort: (columnName: string, nextDirection: Direction) => void;
	direction?: Direction;
}

export function SortableTableHeaderCell({ columnName, onSort, direction, className, children, ...rest }: SortableTableHeaderCellProps) {
	function getNextDirection(): Direction {
		switch (direction) {
			case undefined: // no sort selected, sort in ascending order
				return 'asc';
			case 'asc': // ascending order selected, reverse the order
				return 'desc';
			default: // descending order selected, clear sort
				return undefined;
		}
	}

	function getSortIconClassName(): string {
		if (!direction) {
			return 'text-gray-400';
		}

		if (direction === 'desc') {
			return 'rotate-180';
		}

		return '';
	}

	return (
		<TableHeaderCell className={clsx('cursor-pointer select-none', className)} onClick={() => onSort(columnName, getNextDirection())} {...rest}>
			{children}
			<FontAwesomeIcon className={`ml-2 mt-1 ${getSortIconClassName()}`} icon={direction ? faArrowUp : faArrowDown} />
		</TableHeaderCell>
	);
}

export function NoDataRow(props: HTMLProps<HTMLTableRowElement> & { placeholder?: string }) {
	return (
		<TableRow {...props}>
			<TableCell className="text-center" colSpan={1000}>
				{props.placeholder || 'No results'}
			</TableCell>
		</TableRow>
	);
}

export const handleSort = (
	columnName: string,
	direction: Direction,
	searchParams: URLSearchParams,
	updateUrlSearchParams: (href: string, options?: NavigateOptions | undefined) => void
): void => {
	const params = new URLSearchParams(searchParams);
	const sortByParam = params.get('sort_by');
	const orderByParam = params.get('order_by');

	const sortBy = sortByParam ? sortByParam.split(',') : [];
	const orderBy = orderByParam ? orderByParam.split(',') : [];

	const columnIndex = sortBy.indexOf(columnName);

	if (direction === undefined) {
		if (columnIndex !== -1) {
			sortBy.splice(columnIndex, 1);
			orderBy.splice(columnIndex, 1);
		}
	} else if (columnIndex !== -1) {
		orderBy[columnIndex] = direction;
	} else {
		sortBy.push(columnName);
		orderBy.push(direction);
	}

	if (sortBy.length > 0 && orderBy.length > 0) {
		params.set('sort_by', sortBy.join(','));
		params.set('order_by', orderBy.join(','));
	} else {
		params.delete('sort_by');
		params.delete('order_by');
	}

	updateUrlSearchParams(`?${params.toString()}`);
};

export const getSortDirectionForField = (searchParams: URLSearchParams, fieldName: string): Direction | undefined => {
	const sortBy = searchParams.get('sort_by')?.split(',') || [];
	const orderBy = searchParams.get('order_by')?.split(',') || [];
	const fieldIndex = sortBy.indexOf(fieldName);

	if (fieldIndex > -1) {
		return orderBy[fieldIndex] as Direction;
	}

	return undefined;
};
